import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Header from '../../components/ui/Header';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import WelcomeCard from './components/WelcomeCard';
import QuickActions from './components/QuickActions';
import SavedPropertiesPreview from './components/SavedPropertiesPreview';
import RecentSearches from './components/RecentSearches';
import SearchAlerts from './components/SearchAlerts';
import RecentlyViewed from './components/RecentlyViewed';
import ScheduledTours from './components/ScheduledTours';
import ActivityFeed from './components/ActivityFeed';
import { selectIsAuthenticated } from '../../features/auth/authSlice';
import {
  useGetUserDataQuery, // Reverted to query
  useGetUserSavedPropertiesQuery,
  useGetUserSearchesQuery,
  useGetUserAlertsQuery,
  useGetUserToursQuery,
  useGetUserActivitiesQuery,
  useGetUserFavouritePropertyQuery,
  useGetUserRecentlyViewedQuery
} from '../../features/api/apiSlice';
import Button from '../../components/ui/Button'; // Import Button component

const UserDashboard = () => {
  const navigate = useNavigate();
  // Removed showProfile state as UserProfile is now an independent page

  // Check if user is authenticated
  let isAuthenticated = useSelector(selectIsAuthenticated);

  // Debug authentication state
  console.log('Dashboard Auth State:', {
    isAuthenticated,
    authToken: localStorage.getItem('authToken') ? 'Present' : 'Missing',
    user: localStorage.getItem('user') ? 'Present' : 'Missing'
  });

  // Fetch user data if authenticated
  const { data: userData, isLoading: userLoading, error: userError, refetch: triggerGetUserData } = useGetUserDataQuery(undefined, {
    skip: !isAuthenticated,
  });

  useEffect(() => {
    if (isAuthenticated) {
      triggerGetUserData();
    }
  }, [isAuthenticated, triggerGetUserData]);

  // Fetch dashboard data from APIs only
  const { data: savedProperties = [], isLoading: savedLoading, error: savedError } = useGetUserSavedPropertiesQuery(undefined, {
    skip: !isAuthenticated,
  });
  console.log('Saved Properties Data:', { savedProperties, savedLoading, savedError }); // Debugging line
  const { data: recentSearches = [], isLoading: searchesLoading, error: searchesError } = useGetUserSearchesQuery(undefined, {
    skip: !isAuthenticated,
  });
  const { data: searchAlerts = [], isLoading: alertsLoading, error: alertsError } = useGetUserAlertsQuery(undefined, {
    skip: !isAuthenticated,
  });
  const { data: scheduledTours = [], isLoading: toursLoading, error: toursError } = useGetUserToursQuery(undefined, {
    skip: !isAuthenticated,
  });
  const { data: activities = [], isLoading: activitiesLoading, error: activitiesError } = useGetUserActivitiesQuery(undefined, {
    skip: !isAuthenticated,
  });
  const { data: recentlyViewed = [], isLoading: viewedLoading, error: viewedError } = useGetUserRecentlyViewedQuery(undefined, {
    skip: !isAuthenticated,
  });

  // Debug API responses
  console.log('Dashboard API Responses:', {
    savedProperties: { data: savedProperties, error: savedError },
    recentSearches: { data: recentSearches, error: searchesError },
    searchAlerts: { data: searchAlerts, error: alertsError },
    scheduledTours: { data: scheduledTours, error: toursError },
    activities: { data: activities, error: activitiesError },
    recentlyViewed: { data: recentlyViewed, error: viewedError }
  });



  // Use user data from API or fallback to localStorage
  const user = userData || (isAuthenticated ? JSON.parse(localStorage.getItem('user') || 'null') : null);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  // Loading state for all dashboard data
  const isLoading = userLoading || savedLoading || searchesLoading || alertsLoading || toursLoading || activitiesLoading || viewedLoading;

  // Don't treat authentication errors as dashboard errors - just use fallback user data
  const error = userError && userError.status !== 401 && userError.status !== 403 ? userError : null;


  const handleLogout = () => {
    // Clear authentication data
    localStorage.removeItem('user');
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('authToken');
    localStorage.removeItem('customerId');
    navigate('/login');
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-background">
        <Header user={null} onLogout={handleLogout} />
        <div className="pt-16 flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground mb-4">Please sign in to access your dashboard</h1>
            <p className="text-muted-foreground">You need to be logged in to view your property dashboard.</p>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header user={user} onLogout={handleLogout} />
        <div className="pt-16 flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading your dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header user={user} onLogout={handleLogout} />
        <div className="pt-16 flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground mb-4">Error loading dashboard</h1>
            <p className="text-muted-foreground">There was an error loading your dashboard. Please try again.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} onLogout={handleLogout} />
      
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Breadcrumbs */}
          <div className="mb-6">
            <Breadcrumbs />
          </div>

          {/* Welcome Section */}
          <div className="mb-8">
            <WelcomeCard user={user} />
          </div>

          {/* Quick Actions */}
          <div className="mb-8">
            <QuickActions />
          </div>

          {/* Main Dashboard Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column */}
            <div className="lg:col-span-2 space-y-8">
              <SavedPropertiesPreview savedProperties={savedProperties} />
              <RecentSearches recentSearches={recentSearches} />
              <SearchAlerts searchAlerts={searchAlerts} />
              <RecentlyViewed recentlyViewed={recentlyViewed} />
            </div>

            {/* Right Column */}
            <div className="space-y-8">
              <ScheduledTours scheduledTours={scheduledTours} />
              <ActivityFeed activities={activities} />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default UserDashboard;
